<template>
  <div class="send-flow">
    <!-- Trigger Inputs -->
    <div class="trigger-section" v-if="!generated">
      <div class="field-group">
        <label for="occasion">Occasion</label>
        <select id="occasion" v-model="occasion" class="select-input">
          <option v-for="o in occasions" :key="o" :value="o">{{ o }}</option>
        </select>
      </div>

      <div class="field-group">
        <label for="hint">Any hints? (optional)</label>
        <textarea
          id="hint"
          v-model="hint"
          placeholder="e.g. They just got a new puppy, mention that!"
          class="text-input"
          rows="3"
        ></textarea>
      </div>

      <div class="field-group">
        <label for="senderName">Your name (the from field)</label>
        <input
          id="senderName"
          type="text"
          v-model="senderName"
          placeholder="e.g. Alex"
          class="text-input"
        />
      </div>

      <button
        class="button primary generate-button"
        @click="generate"
        :disabled="generating || !senderName"
      >
        {{ generating ? 'Generating...' : 'Generate Options' }}
      </button>
    </div>

    <!-- Options Display -->
    <div v-if="generated && !selectedOption" class="options-section">
      <h3>Pick your favorite</h3>
      <div class="options-grid">
        <div
          v-for="(option, idx) in options"
          :key="idx"
          class="option-card"
        >
          <div class="option-header">
            <span class="option-vibe">{{ option.vibeLabel }}</span>
            <div class="feedback-buttons">
              <button
                class="feedback-btn"
                :class="{ active: option.feedback === 'up' }"
                @click="giveFeedback(idx, 'up')"
                title="Like this style"
              >
                +
              </button>
              <button
                class="feedback-btn"
                :class="{ active: option.feedback === 'down' }"
                @click="giveFeedback(idx, 'down')"
                title="Not my style"
              >
                -
              </button>
            </div>
          </div>
          <p class="option-message">{{ option.message }}</p>
          <button class="button primary" @click="selectOption(option)">Send this one</button>
        </div>
      </div>
      <button class="button secondary regenerate-button" @click="resetGeneration">
        Try again
      </button>
    </div>

    <!-- Address prompt (if no address on file) -->
    <div v-if="selectedOption && !relationship.hasAddress && !addressConfirmed" class="address-section">
      <h3>We need their address</h3>
      <p>Add a mailing address so we can send the postcard.</p>
      <div class="field-group">
        <label>Address</label>
        <SimpleGoogleAutocomplete
          ref="addressAutocomplete"
          @update:modelValue="handleAddressUpdate"
        />
      </div>
      <div class="field-group">
        <label for="addrOptional">Apt / Suite (optional)</label>
        <input id="addrOptional" type="text" v-model="newAddress.optional" class="text-input" />
      </div>
      <button
        class="button primary"
        :disabled="!newAddress.street || !newAddress.city || !newAddress.postalCode"
        @click="confirmAddress"
      >
        Confirm Address
      </button>
    </div>

    <!-- Final add to cart -->
    <div v-if="selectedOption && (relationship.hasAddress || addressConfirmed)" class="confirm-section">
      <h3>Ready to send!</h3>
      <div class="preview-card">
        <p class="preview-vibe">{{ selectedOption.vibeLabel }}</p>
        <p class="preview-message">{{ selectedOption.message }}</p>
      </div>
      <button class="button primary" :disabled="addingToCart" @click="addToCart">
        {{ addingToCart ? 'Adding...' : 'Add to Cart' }}
      </button>
      <button class="button secondary" :disabled="addingToCart" @click="selectedOption = null">Pick a different one</button>
    </div>

    <Spinner :show="generating || addingToCart" :message="addingToCart ? 'Checking the address...' : 'Our agent is crafting options...'" />
  </div>
</template>

<script>
import Spinner from "@/components/SpinnerComp.vue";
import { AddressAutocomplete as SimpleGoogleAutocomplete } from "@thinking-of-u/ui";
import { generateRelationshipPostcard, recordRelationshipSignal, validateAddress, moderateUserInput } from "@/api";
import { db } from "@/firebase";
import { doc, updateDoc, collection } from "firebase/firestore";

function mapTypeToCategory(type) {
  const map = { friend: "Friends", family: "Family", romantic: "Romantic", partner: "Romantic" };
  return map[type] || "Other";
}

export default {
  name: "RelationshipPostcardFlow",
  components: { Spinner, SimpleGoogleAutocomplete },
  props: {
    relationship: {
      type: Object,
      required: true,
    },
  },
  emits: ["close", "added-to-cart"],
  data() {
    return {
      occasion: "Thinking of U",
      hint: "",
      senderName: "",
      generating: false,
      generated: false,
      addingToCart: false,
      options: [],
      selectedOption: null,
      addressConfirmed: false,
      newAddress: {
        street: "",
        optional: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      },
      occasions: [
        "Thinking of U",
        "Birthday",
        "Anniversary",
        "Just Because",
        "Congratulations",
        "Thank You",
        "Get Well",
        "Missing You",
      ],
    };
  },
  methods: {
    handleAddressUpdate(parsedAddress) {
      if (!parsedAddress) {
        this.newAddress = { ...this.newAddress, street: "", city: "", state: "", postalCode: "", country: "" };
        return;
      }
      this.newAddress = {
        ...this.newAddress,
        street: parsedAddress.street || "",
        city: parsedAddress.city || "",
        state: parsedAddress.state || "",
        postalCode: parsedAddress.postalCode || "",
        country: parsedAddress.country || "",
      };
    },
    async generate() {
      this.generating = true;
      try {
        const { data } = await generateRelationshipPostcard({
          relationshipId: this.relationship.id,
          trigger: {
            occasion: this.occasion,
            hint: this.hint,
          },
          senderName: this.senderName,
        });
        this.options = (data.options || []).map((opt) => ({
          ...opt,
          feedback: null,
        }));
        this.generated = true;
        this.$store.dispatch("setAgentPostcardOptions", this.options);
        // Record generation signal (non-blocking)
        recordRelationshipSignal({
          relationshipId: this.relationship.id,
          type: 'generation_requested',
          payload: {
            occasion: this.occasion,
            hasHint: !!this.hint,
          },
        }).catch(() => {});
      } catch (error) {
        console.error("Generate options error:", error);
        this.$toast.error("Failed to generate options. Please try again.");
      } finally {
        this.generating = false;
      }
    },
    async giveFeedback(idx, direction) {
      const option = this.options[idx];
      option.feedback = direction;
      try {
        await recordRelationshipSignal({
          relationshipId: this.relationship.id,
          type: direction === 'up' ? 'option_liked' : 'option_rejected',
          payload: {
            tone: option.tone,
            style: option.style,
            vibeLabel: option.vibeLabel,
          },
        });
      } catch (error) {
        // Non-blocking feedback - don't interrupt the flow
      }
    },
    selectOption(option) {
      this.selectedOption = option;
      // Record selection signal (non-blocking)
      recordRelationshipSignal({
        relationshipId: this.relationship.id,
        type: 'option_selected',
        payload: {
          tone: option.tone,
          style: option.style,
          vibeLabel: option.vibeLabel,
          occasion: this.occasion,
        },
      }).catch(() => {});
    },
    resetGeneration() {
      // Record regeneration signal (non-blocking)
      recordRelationshipSignal({
        relationshipId: this.relationship.id,
        type: 'regeneration_requested',
        payload: {occasion: this.occasion},
      }).catch(() => {});
      this.generated = false;
      this.options = [];
      this.selectedOption = null;
      this.$store.dispatch("clearAgentPostcardOptions");
    },
    async confirmAddress() {
      try {
        const relationshipsRef = collection(db, "relationships");
        await updateDoc(doc(relationshipsRef, this.relationship.id), {
          address: { ...this.newAddress },
          hasAddress: true,
          updatedAt: Date.now(),
        });
        this.addressConfirmed = true;
      } catch (error) {
        this.$toast.error("Failed to save address. Please try again.");
      }
    },
    isAddressComplete(address) {
      return !!(
        address &&
        typeof address === "object" &&
        address.street &&
        address.city &&
        address.state &&
        address.postalCode &&
        address.country
      );
    },
    async moderateUserInputs() {
      // Moderate the user-supplied free-text that ends up on the card or in the
      // prompt, mirroring the manual PostcardCreation flow. senderName is
      // printed as the from field; hint is the user's free-text instruction.
      const inputsToCheck = [
        { label: "your name", value: this.senderName },
        { label: "hint", value: this.hint },
      ];

      for (const input of inputsToCheck) {
        if (input.value) {
          const { data: moderationResult } = await moderateUserInput({ input: input.value });
          if (!moderationResult.isAllowed) {
            this.$toast.error(`Your ${input.label} contains inappropriate content. Please revise it.`);
            return false;
          }
        }
      }

      return true;
    },
    async addToCart() {
      if (this.addingToCart) return;
      this.addingToCart = true;
      try {
        const address = this.addressConfirmed ? this.newAddress : this.relationship.address;

        // Run the same moderation + address validation the manual flow applies
        // before letting an item reach the shared cart / addPostcardsToQueue.
        const safe = await this.moderateUserInputs();
        if (!safe) return;

        if (!this.isAddressComplete(address)) {
          this.$toast.error("This person's address is incomplete. Please add a full mailing address before sending.");
          return;
        }

        const { data } = await validateAddress({ address });
        if (!data.isValid) {
          this.$toast.error("We couldn't verify this address. Please double-check it before sending.");
          return;
        }

        const nameParts = this.relationship.name.split(" ");
        const cartItem = {
          recipientFirstName: nameParts[0],
          recipientLastName: nameParts.slice(1).join(" ") || "",
          fromField: this.senderName,
          message: this.selectedOption.message,
          tone: this.selectedOption.vibeLabel,
          style: this.selectedOption.style || "Agent",
          occasion: this.occasion,
          address: address,
          agentGenerated: true,
          relationshipId: this.relationship.id,
          category: mapTypeToCategory(this.relationship.type),
          recipient: this.relationship.type,
          userIntent: "",
        };
        this.$store.dispatch("addToCart", cartItem);
        this.$store.dispatch("clearAgentPostcardOptions");
        this.$emit("added-to-cart");
      } catch (error) {
        this.$toast.error("Failed to add to cart. Please try again.");
      } finally {
        this.addingToCart = false;
      }
    },
  },
};
</script>

<style scoped>
.send-flow {
  max-width: 600px;
  margin: 0 auto;
  padding: 10px;
}

.field-group {
  margin-bottom: 16px;
}

.field-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--rich-black);
}

.text-input,
.select-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.95rem;
  box-sizing: border-box;
}

.text-input:focus,
.select-input:focus {
  outline: none;
  border-color: var(--hollywood-cerise);
}

.generate-button {
  width: 100%;
  margin-top: 8px;
}

.options-section h3 {
  margin: 0 0 16px;
  color: var(--rich-black);
}

.options-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.option-card {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 16px;
  background-color: #fff;
  transition: box-shadow 0.3s;
}

.option-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.option-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.option-vibe {
  background-color: #ffe6ed;
  color: var(--hollywood-cerise);
  padding: 3px 12px;
  border-radius: 14px;
  font-size: 0.8rem;
  font-weight: 600;
}

.feedback-buttons {
  display: flex;
  gap: 6px;
}

.feedback-btn {
  width: 30px;
  height: 30px;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  background-color: #fff;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: var(--rich-black);
}

.feedback-btn:hover {
  border-color: var(--hollywood-cerise);
}

.feedback-btn.active {
  background-color: var(--hollywood-cerise);
  color: white;
  border-color: var(--hollywood-cerise);
}

.option-message {
  margin: 0 0 12px;
  color: var(--violet-lush);
  font-size: 0.9rem;
  line-height: 1.5;
}

.regenerate-button {
  margin-top: 16px;
  width: 100%;
}

.address-section {
  margin-top: 16px;
}

.address-section h3 {
  margin: 0 0 8px;
  color: var(--rich-black);
}

.address-section p {
  margin: 0 0 16px;
  color: var(--violet-lush);
  font-size: 0.9rem;
}

.address-row {
  display: flex;
  gap: 12px;
}

.address-row .field-group {
  flex: 1;
}

.confirm-section {
  margin-top: 16px;
}

.confirm-section h3 {
  margin: 0 0 12px;
  color: var(--rich-black);
}

.preview-card {
  border: 2px solid var(--hollywood-cerise);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: #fff;
}

.preview-vibe {
  margin: 0 0 8px;
  color: var(--hollywood-cerise);
  font-weight: 600;
  font-size: 0.85rem;
}

.preview-message {
  margin: 0;
  color: var(--violet-lush);
  line-height: 1.5;
}

.confirm-section .button {
  width: 100%;
  margin-bottom: 8px;
}

.button {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.button.primary {
  background-color: var(--accent2-color);
  color: white;
}

.button.primary:hover {
  background-color: var(--accent-color);
}

.button.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button.secondary {
  background-color: var(--hollywood-cerise);
  color: white;
}

.button.secondary:hover {
  background-color: #e41393;
}

@media (max-width: 768px) {
  .address-row {
    flex-direction: column;
    gap: 0;
  }
}
</style>
